import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback
} from 'react'
import { isEmpty } from 'ramda'
import styled from 'styled-components'
import {
  Box,
  Flex,
  FormField,
  Label,
  Select,
  getPaletteColor,
  Text
} from 'pcln-design-system'
import { useRouter } from 'next/router'
import { Calendar } from '@pcln/date-picker'
import { AirPricedCalendar } from '@pcln/fly-date-picker'
import { useForm } from 'react-hook-form'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import ShadowEffect from '@/components/ShadowEffect'
import TypeAhead from '@/components/TypeAhead'
import { PopoverContainer } from '@/components/Popover'
import { FLIGHTS_DISCLAIMER } from '@/components/TravelerSelection/constants'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useTabGroup from '@/hooks/useTabGroup'
import SearchFormButton from '@/components/SearchFormButton'
import { getDateRange } from '@/shared-utils/date-helper'
import {
  getDateFieldBorderColors,
  getDateFieldErrorMessage
} from '@/shared-utils/error-helpers'
import { LOCATION_SEARCH_TYPE, QueryString } from '@/types'
import analytics from '@/shared-utils/analytics'
import {
  ANALYTICS_ACTION_FLIGHTS_SEARCH,
  ANALYTICS_CATEGORIES,
  INVALID
} from '@/constants/analytics'
import AmbiguousCityLink from '@/components/AmbiguousCityLink'
import BundleAndSave from '@/components/BundleAndSave/BundleAndSave'
import { yupResolver } from '@hookform/resolvers/yup'
import MultiDestination from './MultiDestination'
import {
  ADD_PRODUCT_OPTIONS,
  CABIN_CLASS,
  FLIGHT_TYPE_INFO,
  HOTEL,
  MULTI_DESTINATION,
  ONE_WAY,
  ROUND_TRIP
} from '../constants'
import {
  FlightRecordType,
  FlightFormStateType,
  CABIN_CLASS_TYPE,
  FLIGHT_TYPE_INFO as FLIGHT_TYPE_INFO_TYPE,
  FlightDateType
} from '../types'
import searchFormSchema from './validation'
import FlightTypeRadios from './FlightTypeRadios'
import FlightFormBanner from './FlightFormBanner'
import {
  setFlights,
  getPrevDate,
  isRoundTrip,
  isOneWay,
  isMultiDestination,
  includesStay,
  isPackage,
  fields,
  initialState,
  flightsStartLocationSearchKey,
  flightsEndLocationSearchKey,
  flightsDateRangeKey
} from './utils'
import RebookBanner from './RebookBanner'

const StyledSelect = styled(Select)`
  background-color: ${props =>
    props.isRebook
      ? getPaletteColor('background.tint')
      : getPaletteColor('background.lightest')};
  pointer-events: ${props => (props.isRebook ? 'none' : 'auto')};
  cursor: pointer;
  height: 56px;
`

type FormType = {
  isRebook?: boolean
  onSubmit: (data: FlightFormStateType, queryParams?: QueryString) => void
  flightTypes?: FLIGHT_TYPE_INFO_TYPE[]
  promoDeal?: boolean
}

export default function Form({
  isRebook = false,
  onSubmit,
  flightTypes = [
    FLIGHT_TYPE_INFO[ROUND_TRIP],
    FLIGHT_TYPE_INFO[ONE_WAY],
    FLIGHT_TYPE_INFO[MULTI_DESTINATION]
  ],
  promoDeal
}: FormType) {
  const { registerTabElement, focusNextElement } = useTabGroup()

  const { isMobile, prePopData, webstats, customer, flightRebookDetails } =
    useContext(BootstrapDataContext)

  const {
    flights,
    flightType,
    cabinClass,
    adultCount,
    youthCount,
    childrenCount,
    infantCount
  } = prePopData?.flights ?? {}

  const initialValue: FlightFormStateType = {
    ...initialState,
    ...(flights && { flights }),
    ...(flightType && { flightType }),
    ...(cabinClass && { cabinClass }),
    ...(adultCount && { adultCount }),
    ...(youthCount && { youthCount }),
    ...(childrenCount && { childrenCount }),
    ...(infantCount && { infantCount }),
    ...(isRebook &&
      flightRebookDetails && {
        cabinClass: flightRebookDetails.cabinClass,
        ...flightRebookDetails.passengerCount
      })
  }
  const rebookCarrierName = flightRebookDetails?.carrierName ?? ''
  const rebookCreditAmount = flightRebookDetails?.creditAmount ?? ''

  const {
    handleSubmit,
    register,
    setValue,
    trigger,
    unregister,
    watch,
    formState: { errors }
  } = useForm<FlightFormStateType>({
    defaultValues: initialValue,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })
  const {
    adultCount: watchAdultCount,
    cabinClass: watchCabinClass,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    infantCount: watchInfantCount,
    flightType: watchFlightType,
    flights: watchFlights,
    roomCount: watchRoomCount,
    tripType: watchTripType,
    youthCount: watchYouthCount
  } = watch()

  const { query, pathname } = useRouter()
  const {
    'ambig-origin-city': ambigOriginCity,
    'ambig-dest-city': ambigDestCity
  } = query
  const isHomepage = pathname === '/'
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  useEffect(() => {
    fields.forEach(name => {
      register(name)
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  useEffect(() => {
    trigger('roomCount')
  }, [
    trigger,
    watchAdultCount,
    watchChildrenCount,
    watchInfantCount,
    watchTripType,
    watchRoomCount,
    watchYouthCount
  ])

  useEffect(() => {
    if (!isEmpty(errors)) {
      trigger('flights')

      let gaLabel = `${INVALID} -`
      if (errors.flights) {
        errors.flights.forEach?.((flight, index) => {
          const invalidFlightFields = Object.keys(flight || {})
          gaLabel += ` Flight ${index + 1}: ${invalidFlightFields.join('| ')}`
        })
      }
      if (errors.roomCount) gaLabel += ' | roomCount'
      if (errors.infantCount) gaLabel += ' | infantCount'

      analytics.fireEvent({
        action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
        category: ANALYTICS_CATEGORY,
        label: gaLabel
      })
    }
  }, [errors, trigger, watchFlights, ANALYTICS_CATEGORY])

  useEffect(() => {
    trigger('infantCount')
  }, [
    trigger,
    watchFlights,
    watchInfantCount,
    watchAdultCount,
    watchChildrenAges
  ])

  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)
  const [departureAirport, setDepartureAirport] =
    useState<LOCATION_SEARCH_TYPE | null>(null)
  const [arrivalAirport, setArrivalAirport] =
    useState<LOCATION_SEARCH_TYPE | null>(null)
  const startLocationPlaceholder = 'Departing from?'
  const endLocationPlaceholder = 'Going to?'
  const isFlightTypeRoundTrip = isRoundTrip(watchFlightType)
  const isFlightTypeOneWay = isOneWay(watchFlightType)
  const isFlightTypeMultiDestination = isMultiDestination(watchFlightType)
  const isFlightTypeIncludeStay = includesStay(watchFlightType)
  const showCabinClass =
    (!isFlightTypeRoundTrip || !isPackage(watchTripType)) && !promoDeal
  const showBundleAndSave = isFlightTypeRoundTrip && !promoDeal
  const firstFlight: Partial<FlightRecordType> = watchFlights[0] || {}
  const datePickerLabel = isFlightTypeRoundTrip
    ? 'Departing - Returning'
    : 'Departing'
  const datePickerAriaLabel =
    isFlightTypeOneWay || isFlightTypeMultiDestination
      ? 'Departing Date Picker'
      : 'Departing – Returning Date Picker'

  const maxDaysHotelToSelect = 30

  const isPricedCalendarActive = !isFlightTypeMultiDestination

  const CalendarComponent = isPricedCalendarActive
    ? AirPricedCalendar
    : Calendar

  const [showOriginMoreCityLink, setShowOriginMoreCityLink] = useState(true)
  const origin = firstFlight?.startLocation?.cityName
  const showOriginDisambiguation =
    !!ambigOriginCity && !!origin && !isHomepage && showOriginMoreCityLink

  const ambiguousOriginElement = useRef<HTMLInputElement>()
  function registerOriginAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousOriginElement.current = ref
  }

  const [showDestMoreCityLink, setShowDestMoreCityLink] = useState(true)
  const destination = firstFlight?.endLocation?.cityName
  const showDestDisambiguation =
    !!ambigDestCity && destination && !isHomepage && showDestMoreCityLink

  const ambiguousDestElement = useRef<HTMLInputElement>()
  function registerDestAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousDestElement.current = ref
  }

  const clarifyAmbiguity = useCallback(
    (
      element: React.MutableRefObject<HTMLInputElement | undefined>,
      flight: Partial<FlightRecordType>,
      locationType: 'endLocation' | 'startLocation'
    ) => {
      const location = flight[locationType]

      return setTimeout(() => {
        if (element.current && location) {
          element.current.focus()
          setValue(
            'flights',
            [
              {
                ...flight,
                [locationType]: {
                  ...location,
                  itemName: location.cityName
                }
              }
            ],
            { shouldDirty: true }
          )
        }
      }, 0)
    },
    [setValue]
  )
  const typeaheadWidth = [1, null, 1 / 2, null, 1 / 3]
  const searchButtonContainerWidth = [
    1,
    null,
    showCabinClass ? 1 : 1 / 2,
    null,
    showCabinClass ? 1 / 3 : 1 / 2
  ]

  const bundleAndSaveMobileWidth = [
    1,
    null,
    showCabinClass ? 1 : 1 / 2,
    null,
    showCabinClass ? 1 / 3 : 1 / 2
  ]

  if (!isFlightTypeMultiDestination) {
    typeaheadWidth[4] = 1 / 2
    searchButtonContainerWidth[4] = 1 / 2
    bundleAndSaveMobileWidth[4] = 1 / 2
  }

  return (
    <form
      onSubmit={handleSubmit(values =>
        onSubmit({ ...values, gaCategory: ANALYTICS_CATEGORY }, query)
      )}
      data-testid={promoDeal ? 'express-deal-form' : 'form'}
      aria-label={
        promoDeal ? 'flight-express-deal-search-form' : 'flight-search-form'
      }
    >
      <Flex mb={2}>
        <FlightTypeRadios
          flightTypesToDisplay={flightTypes}
          flightType={watchFlightType}
          setValue={setValue}
          flights={watchFlights}
        />
      </Flex>
      <Flex key={0} mx={-2} flexWrap="wrap" pb={2}>
        <Box width={typeaheadWidth} py={[0, null, 2]} px={2}>
          <ShadowEffect enabled forProperty={flightsStartLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct="flights"
                placeholder={startLocationPlaceholder}
                label={startLocationPlaceholder}
                searchKey={flightsStartLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.startLocation ?? {}}
                disableShadowState={disableShadowState}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                  if (showOriginDisambiguation) {
                    registerOriginAmbiguousElement(elem)
                  }
                }}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      startLocation: item
                    },
                    setValue
                  )
                  setDepartureAirport(item)
                  if (item?.cityName !== origin) {
                    setShowOriginMoreCityLink(false)
                  }
                  if (!isMobile && !!item) {
                    focusNextElement()
                  }
                }}
              />
            )}
          </ShadowEffect>
          {showOriginDisambiguation && (
            <AmbiguousCityLink
              city={origin}
              handleCallback={() => {
                clarifyAmbiguity(
                  ambiguousOriginElement,
                  firstFlight,
                  'startLocation'
                )
                analytics.fireEvent({
                  action: 'ambiguous-city',
                  category: ANALYTICS_CATEGORY,
                  label: `${origin} - flight origin`
                })
              }}
            />
          )}
        </Box>
        <Box width={typeaheadWidth} py={[0, null, 2]} px={2}>
          <ShadowEffect enabled forProperty={flightsEndLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct="flights"
                placeholder={endLocationPlaceholder}
                label={endLocationPlaceholder}
                searchKey={flightsEndLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.endLocation ?? {}}
                disableShadowState={disableShadowState}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                  if (showDestDisambiguation) {
                    registerDestAmbiguousElement(elem)
                  }
                }}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      endLocation: item
                    },
                    setValue
                  )
                  setArrivalAirport(item)
                  if (item?.cityName !== destination) {
                    setShowDestMoreCityLink(false)
                  }
                  if (!isMobile && !!item) {
                    focusNextElement()
                  }
                }}
              />
            )}
          </ShadowEffect>
          {showDestDisambiguation && (
            <AmbiguousCityLink
              city={destination}
              handleCallback={() => {
                clarifyAmbiguity(
                  ambiguousDestElement,
                  firstFlight,
                  'endLocation'
                )
                analytics.fireEvent({
                  action: 'ambiguous-city',
                  category: ANALYTICS_CATEGORY,
                  label: `${destination} - flight destination`
                })
              }}
            />
          )}
        </Box>
        <Flex
          alignItems="baseline"
          justifyContent="stretch"
          width={
            showCabinClass
              ? [1, null, null, null, 1 / 3]
              : [1, null, 1 / 2, null, 1 / 2]
          }
          py={[0, null, null, 2]}
          px={2}
        >
          <ShadowEffect forProperty={flightsDateRangeKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <PopoverContainer
                key="dates"
                data-calendar
                open={isCalendarOpen}
                position="left"
                onDismiss={() => {
                  setIsCalendarOpen(false)
                  disableShadowState()
                }}
              >
                <DateField
                  m={0}
                  width={1 / 2}
                  id={flightsDateRangeKey(0)}
                  name={flightsDateRangeKey(0)}
                  ref={(elem: HTMLInputElement) => {
                    registerTabElement(elem)
                  }}
                  label={datePickerLabel}
                  placeholder={datePickerLabel}
                  value={getDateRange(
                    {
                      departureDate: firstFlight?.startDate ?? null,
                      arrivalDate: firstFlight?.endDate ?? null
                    },
                    !isFlightTypeRoundTrip
                  )}
                  onFocus={() => {
                    setIsCalendarOpen(true)
                    // analytics call for handleCalendarOnFocus(`datefield ${i}`)
                  }}
                  error={getDateFieldErrorMessage(
                    errors,
                    0,
                    isFlightTypeRoundTrip
                  )}
                  errorZIndex={1}
                  hideTooltip={
                    isCalendarOpen ||
                    ((isFlightTypeOneWay || isFlightTypeMultiDestination) &&
                      !!firstFlight.startDate) ||
                    (isFlightTypeRoundTrip &&
                      !!firstFlight.startDate &&
                      !!firstFlight.endDate)
                  }
                  color={getDateFieldBorderColors(errors, 0)}
                  data-testid={`FLIGHTS_TRAVEL_DATES_INPUT_${0}`}
                  aria-label={datePickerAriaLabel}
                  aria-expanded={isCalendarOpen ? 'true' : 'false'}
                />
                <CalendarWrapperBox
                  boxShadowSize={!isMobile && 'overlay-lg'}
                  borderRadius={!isMobile && '2xl'}
                >
                  <CalendarComponent
                    roundedCorners
                    monthWidthPx={350}
                    id={`flight-date-range-calendar-${0}`}
                    onChange={(
                      _e: React.ChangeEvent,
                      dates: FlightDateType
                    ) => {
                      const { startDate = '', endDate = '' } = dates
                      const dateObj = {
                        startDate,
                        ...(isFlightTypeRoundTrip && { endDate })
                      }
                      setFlights(watchFlights, dateObj, setValue)
                      if (
                        !isFlightTypeRoundTrip ||
                        (dates.startDate && dates.endDate)
                      ) {
                        disableShadowState()
                        setIsCalendarOpen(false)
                      }
                    }}
                    minDate={getPrevDate(watchFlights, 0)}
                    maxDaysToSelect={
                      watchTripType.includes(HOTEL) && isFlightTypeRoundTrip
                        ? maxDaysHotelToSelect
                        : undefined
                    }
                    startDate={firstFlight.startDate}
                    endDate={
                      isFlightTypeRoundTrip ? firstFlight.endDate : undefined
                    }
                    selectTwoDates={isFlightTypeRoundTrip}
                    allowSameDay={!isFlightTypeIncludeStay}
                    flightDetails={{
                      cabinClass: watchCabinClass,
                      flights: [
                        {
                          departureAirport,
                          arrivalAirport
                        }
                      ],
                      tripType: watchFlightType
                    }}
                    variantName="EXPRESS"
                    useDefaultFooter={isFlightTypeMultiDestination}
                    onDismiss={() => {
                      disableShadowState()
                      setIsCalendarOpen(false)
                    }}
                    useColorCode={!isFlightTypeMultiDestination}
                    callFlexDatesGraphUrl
                  />
                </CalendarWrapperBox>
              </PopoverContainer>
            )}
          </ShadowEffect>
        </Flex>
        {isFlightTypeMultiDestination && (
          <Box p={2} width={1}>
            <MultiDestination
              flights={watchFlights}
              setValue={setValue}
              errors={errors}
              isMobile={isMobile}
            />
          </Box>
        )}
        <Box
          width={[1, null, 1 / 2, null, showCabinClass ? 1 / 3 : 1 / 2]}
          py={[0, null, 2]}
          px={2}
        >
          <ShadowEffect
            enabled
            forProperty="travelers-selection-readonly-input"
            alternativeBlurHandling
          >
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TravelerSelection
                disabled={isRebook}
                adultCount={watchAdultCount}
                childrenAges={watchChildrenAges}
                childrenCount={watchChildrenCount}
                disableShadowState={disableShadowState}
                disclaimer={FLIGHTS_DISCLAIMER}
                infantCount={watchInfantCount}
                isMobile={isMobile}
                onDoneButtonClick={disableShadowState}
                roomCount={watchRoomCount}
                setValue={setValue}
                showChildAgeSelectors={false}
                showInfantMenuItem
                showRoomMenuItem={watchTripType.includes(HOTEL)}
                showSubLabel
                showYouthMenuItem
                youthCount={watchYouthCount}
                error={
                  errors?.infantCount?.message ||
                  errors?.roomCount?.message ||
                  ''
                }
              />
            )}
          </ShadowEffect>
        </Box>
        {showCabinClass && (
          <Box width={[1, null, 1 / 2, null, 1 / 3]} p={2} mt={[2, null, 0]}>
            <ShadowEffect forProperty="cabin-class-select">
              {(
                { disableShadowState }: { disableShadowState: () => void } // eslint-disable-line react/no-unused-prop-types
              ) => (
                <FormField>
                  <Label
                    autoHide
                    htmlFor="cabin-class-select"
                    color={isRebook ? getPaletteColor('text.base') : undefined}
                  >
                    Cabin Class
                  </Label>
                  <StyledSelect
                    isRebook={isRebook}
                    disabled={isRebook}
                    isMobile={isMobile}
                    m={0}
                    id="cabin-class-select"
                    name="cabinClass"
                    value={watchCabinClass}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setValue(
                        'cabinClass',
                        e.target.value as CABIN_CLASS_TYPE,
                        { shouldDirty: true }
                      )
                      disableShadowState()
                      /**  for GA tracking */
                    }}
                  >
                    {Object.keys(CABIN_CLASS).map(name => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </StyledSelect>
                </FormField>
              )}
            </ShadowEffect>
          </Box>
        )}
        <FlightFormBanner
          isMobile={isMobile}
          isRebook={isRebook}
          bundleAndSaveMobileWidth={bundleAndSaveMobileWidth}
          isFlightTypeMultiDestination={isFlightTypeMultiDestination}
          showBundleAndSave={showBundleAndSave}
          bundleAndSave={
            <Box height="56px">
              <BundleAndSave
                category={ANALYTICS_CATEGORY}
                productOptions={ADD_PRODUCT_OPTIONS}
                tripType={watchTripType}
                label="Bundle + Save"
                setValue={setValue}
              />
            </Box>
          }
          rebookBanner={
            <RebookBanner
              credit={rebookCreditAmount}
              airline={rebookCarrierName}
            />
          }
        />
        <Box width={searchButtonContainerWidth} p={2} pb={0}>
          <SearchFormButton
            buttonText="Find Your Flight"
            data-testid="FLIGHTS_SUBMIT_BUTTON"
            handleClick={() => {
              if (isMobile) {
                window?.PCLN?.pclnEventDispatcher?.dispatch('FIND_YOUR_FLIGHT')
              }
            }}
            data-rudder-event
            data-sub-type="search"
            data-source={isHomepage ? 'homepage' : 'flight landing'}
            data-parent-product="FLY"
            data-child-product={watchTripType.split(':').slice(1).join(':')}
            data-cguid={webstats.clientGUID}
            data-email-address={customer.emailAddress}
          />
          <Box
            style={{
              left: !isMobile && isFlightTypeRoundTrip ? 0 : undefined,
              right: !isMobile && isFlightTypeRoundTrip ? 0 : undefined
            }}
            pb={[3, null, 0]}
          >
            <Text
              fontSize={0}
              textAlign={!isFlightTypeMultiDestination ? 'center' : 'left'}
              mt={isMobile ? '12px' : 2}
            >
              Book a flight with free cancellation for flexibility
            </Text>
          </Box>
        </Box>
      </Flex>
    </form>
  )
}
